import { type RefObject, useLayoutEffect, useRef } from 'react';
import { toRem } from './useFixedHeaderClearance';

/** Where the element starts, in rem; read it as `var(--fill-top)`. */
export const FILL_TOP_VAR = '--fill-top';

/**
 * Distance from the top of the page to the element's top edge. A fixed
 * element does not move with the page, so its viewport position is the
 * answer; anything in flow adds the scroll offset.
 */
export const pageTop = (element: Element): number => {
  const { top } = element.getBoundingClientRect();
  return getComputedStyle(element).position === 'fixed'
    ? top
    : top + window.scrollY;
};

/**
 * Publish where the element starts, so its CSS can size it to the bottom of
 * the window: `height: calc(100dvh - var(--fill-top) - <gap>)`.
 *
 * The space above is not a constant. The header's height differs by persona
 * and the "Not Production" strip comes and goes, so a fixed offset is right
 * for at most one of those. Kept in rem, like `useFixedHeaderClearance`, so
 * it scales with the root font. Re-measures on resize, when the document's
 * height changes, and when the header or the strip under it changes size.
 *
 * Pass `existing` when the element already has a ref for something else.
 */
export const useFillToViewportBottom = <T extends HTMLElement>(
  existing?: RefObject<T>
) => {
  const own = useRef<T>(null);
  const ref = existing ?? own;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const rootFontPx = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const top = toRem(pageTop(element), rootFontPx);
      if (top) element.style.setProperty(FILL_TOP_VAR, top);
    };

    measure();
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    // Something above can shrink without the body changing height (the page
    // below fills to the window either way), so watch the header and the
    // environment strip too: they are what moves this element.
    document
      .querySelectorAll('header, .MuiAppBar-root, .main-root-wrapper > *')
      .forEach((above) => {
        if (!above.contains(element)) observer.observe(above);
      });

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
};
